/**
 * Palette LUT builders for the "Mosaic GL" render path's palette toggle
 * (mosaicGlController.ts's `PaletteMode`): both variants are built from
 * palette.ts's own `colorForDbz`/`decodeDbz` (the single source of truth
 * every other render path already uses — station drill-down, the CPU mosaic
 * path's mosaicColorize.ts, and the fusion WebGL path's PALETTE_LUT texture),
 * never re-derived. What actually differs between "banded" and "continuous"
 * isn't the underlying ramp — palette.ts's `interp1d` was always a smooth,
 * piecewise-linear ramp, not a step function — it's how mosaicGlCompositor.ts
 * SAMPLES the resulting texture:
 *
 *  - banded:     this file's `buildBandedPaletteLut()` is literally
 *                palette.ts's own `PALETTE_LUT` (256 entries, one per raw
 *                byte), bound NEAREST — so a smoothed, fractional
 *                interpolated raw value (mosaicGlShader.ts's data-space
 *                bilinear/bicubic kernel) still snaps to one of 256 discrete
 *                colors: the classic stepped NWS look, now applied
 *                downstream of continuous data-space smoothing instead of
 *                baked into flat dBZ-range blocks.
 *  - continuous: `buildContinuousPaletteLut()` resamples the SAME ramp at
 *                finer (default 1024-entry) resolution across the raw-byte
 *                domain, bound LINEAR — hardware bilinear LUT filtering
 *                removes the last per-raw-unit color step the banded LUT
 *                still has at NEAREST-filtered 256-entry resolution, which
 *                is what "continuous ... in dBZ space" means in practice.
 *
 * Both are pure Uint8ClampedArray builders (mosaicGlCompositor.ts owns the
 * DOM/GL texture upload), same testability split as mosaicColorize.ts's
 * colorizeRawBand/rasterToDataUrl.
 */
import { PALETTE_LUT, colorForDbz, decodeDbz } from "./palette";

export const BANDED_LUT_SIZE = 256;
export const CONTINUOUS_LUT_SIZE = 1024;

/** `palette.ts`'s own 256-entry raw-byte -> RGBA table, reused verbatim (not
 * rebuilt) so the banded LUT is byte-identical to every other render path's
 * palette by construction, not just "close." */
export function buildBandedPaletteLut(): Uint8ClampedArray {
  return PALETTE_LUT;
}

/** `size` evenly-spaced samples across the full raw-byte domain [0, 255]
 * (texel `i` represents `raw = (i / (size-1)) * 255`, so `size = 256` lands
 * exactly on integer raw bytes — see mosaicGlPalette.test.ts's cross-check
 * against `buildBandedPaletteLut()`/`PALETTE_LUT` at that size), each
 * colorized via `colorForDbz(decodeDbz(raw))` — the exact same per-value
 * formula `PALETTE_LUT`'s own build loop uses, just evaluated at a
 * continuous (possibly fractional) raw value instead of palette.ts's native
 * integer steps. */
export function buildContinuousPaletteLut(size: number = CONTINUOUS_LUT_SIZE): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(size * 4);
  for (let i = 0; i < size; i++) {
    const raw = (i / (size - 1)) * 255;
    const [r, g, b, a] = colorForDbz(decodeDbz(raw));
    lut[i * 4] = r;
    lut[i * 4 + 1] = g;
    lut[i * 4 + 2] = b;
    lut[i * 4 + 3] = a;
  }
  return lut;
}
